import React, { useState, useEffect } from "react"
import { visit } from "unist-util-visit"
import { toHtml } from "hast-util-to-html"
import { graphql } from "gatsby"
import { useTranslation, Link } from "gatsby-plugin-react-i18next"
import { Events, scrollSpy } from "react-scroll"
import { PDFDownloadLink } from "@react-pdf/renderer"
import { gfm } from "micromark-extension-gfm"
import { fromMarkdown } from "mdast-util-from-markdown"
import { gfmFromMarkdown } from "mdast-util-gfm"
import Layout from "../components/layout"
import Toc from "../components/toc"
import PdfDocument from "../components/pdfDocument"
import PageButtons from "../components/pageButtons"

const Page = ({ data, location }) => {
  const [html, setHtml] = useState({})
  const [headings, setHeadings] = useState([])
  const [mdast, setMdast] = useState(null)
  const { t } = useTranslation()
  const headerSizes = {
    2: "text-[14px] pl-[1em] font-bold",
    3: "text-[14px] pl-[2em] font-normal",
    4: "text-[14px] pl-[3em] font-light",
  }
  useEffect(() => {
    const page = data.markdownRemark
    const headings =
      page.headings &&
      page.headings.filter(heading => heading.depth < 5 && heading.depth > 1)
    const Ast = page.htmlAst
    visit(Ast, "element", node => {
      if (node.tagName === "section") {
        Object.assign(node.properties, { id: node.children[0].properties.id })
        delete node.children[0].properties.id
      }
    })
    const mdast = fromMarkdown(page.rawMarkdownBody, {
      extensions: [gfm()],
      mdastExtensions: [gfmFromMarkdown()],
    })
    setHtml(toHtml(Ast))
    setHeadings(headings)
    setMdast(mdast)
    Events.scrollEvent.register("begin", (to, element) => {
      return null
    })

    Events.scrollEvent.register("end", (to, element) => {
      return null
    })

    scrollSpy.update()
  }, [data.markdownRemark])
  return (
    <Layout location={location}>
      <div className="flex m-auto py-12 w-10/12 md:w-10/12 lg:w-9/12 xl:w-7/12 relative">
        <article className="relative w-full sm:max-w-3xl pb-[50vw] prose prose-p:text-dark-sage prose-a:text-dark-sage prose-a:bg-light-green prose-headings:text-dark-sage prose-strong:text-dark-sage prose-li:text-dark-sage prose-headings:font-['Archivo-Bold']">
          <Link to={`/`} className="-ml-10 absolute">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-8 h-8 rotate-90 text-dark-sage hover:text-darker-sage"
            >
              <title>{t("back")}</title>
              <path
                fillRule="evenodd"
                d="M12.53 16.28a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 011.06-1.06L12 14.69l6.97-6.97a.75.75 0 111.06 1.06l-7.5 7.5z"
                strokeWidth="10px"
                clipRule="evenodd"
              />
            </svg>
          </Link>

          <div dangerouslySetInnerHTML={{ __html: html }} />
          <div className="mt-24 flex justify-end">
            {mdast ? (
              <PDFDownloadLink
                document={<PdfDocument mdast={mdast} />}
                fileName={`${data.markdownRemark.frontmatter.title
                  .replace(/([a-z])([A-Z])/g, "$1-$2")
                  .replace(/[\s_|,]+/g, "-")
                  .toLowerCase()}.pdf`}
                className="px-3 py-1 bg-gray-200 text-purple-700 hover:bg-ddp-green hover:text-white no-underline rounded-md flex"
              >
                {({ blob, url, loading, error }) =>
                  loading ? (
                    t("generating_pdf")
                  ) : (
                    <div
                      className="flex"
                      title={t("generate_pdf")}
                      alt={t("generate_pdf")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-6 h-6 mr-2"
                      >
                        <title>{t("generate_pdf")}</title>
                        <path
                          fillRule="evenodd"
                          d="M19.5 21a3 3 0 003-3V9a3 3 0 00-3-3h-5.379a.75.75 0 01-.53-.22L11.47 3.66A2.25 2.25 0 009.879 3H4.5a3 3 0 00-3 3v12a3 3 0 003 3h15zm-6.75-10.5a.75.75 0 00-1.5 0v4.19l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V10.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {t("generate_pdf")}
                    </div>
                  )
                }
              </PDFDownloadLink>
            ) : null}
          </div>
        </article>
        <div className="relative">
          {headings.length > 0 && (
            <Toc
              headings={headings}
              headerSizes={headerSizes}
              title={data.markdownRemark.frontmatter.title}
            />
          )}
          <PageButtons />
        </div>
      </div>
    </Layout>
  )
}

export const query = graphql`
  query ($slug: String!, $language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    markdownRemark(
      fields: { slug: { eq: $slug } }
      frontmatter: { language: { eq: $language } }
    ) {
      rawMarkdownBody
      htmlAst
      headings {
        value
        depth
        id
      }
      frontmatter {
        title
      }
    }
  }
`
export default Page
